import { formatDistanceToNow } from 'date-fns'
import { Bell } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar'
import { cn } from 'lib/utils'

import type { NotificationItem as NotificationItemData } from '../types/notification.types'

/** "5 minutes ago", or nothing for a timestamp that does not parse. */
const when = (iso: string) => {
	const date = new Date(iso)
	return Number.isNaN(date.getTime()) ? '' : formatDistanceToNow(date, { addSuffix: true })
}

const initials = (name: string) =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(part => part[0]?.toUpperCase())
		.join('')

export interface NotificationItemProps {
	item: NotificationItemData
	onSelect: (item: NotificationItemData) => void
}

/** One row in the bell's list (MYD-542): who, what, when, and a dot while unread. */
export function NotificationItem({ item, onSelect }: NotificationItemProps) {
	const unread = !item.readAt

	return (
		<button
			type="button"
			onClick={() => onSelect(item)}
			aria-label={`${unread ? 'Unread: ' : ''}${item.title}`}
			className={cn(
				'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50',
				'focus:outline-none focus-visible:bg-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0080fe]',
				unread && 'bg-primary/5',
			)}
		>
			<Avatar className="h-8 w-8 shrink-0">
				{item.actor?.image ? <AvatarImage src={item.actor.image} alt="" /> : null}
				<AvatarFallback className="bg-gray-100 text-xs text-gray-500">
					{item.actor?.name ? initials(item.actor.name) : <Bell className="h-4 w-4" />}
				</AvatarFallback>
			</Avatar>

			<span className="min-w-0 flex-1">
				<span className={cn('block text-sm text-gray-900', unread && 'font-semibold')}>{item.title}</span>
				{item.body && <span className="mt-0.5 block line-clamp-2 text-xs text-gray-500">{item.body}</span>}
				<span className="mt-1 block text-xs text-gray-400">{when(item.createdAt)}</span>
			</span>

			{unread && <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#0080fe]" />}
		</button>
	)
}

export default NotificationItem
